import React, { useCallback, useEffect, useState, useContext, useRef } from 'react';
import { Grid } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { getContasAPagarConfirmadas } from '../apis/apiContasaAPagar';
import { getPropriedades } from '../apis/apiPropriedades';
import { getRebanhoMedio } from '../apis/apiAnimais';
import { showFilters } from '../utils/configFilters';
import { LanguageContext } from '../App';
import dayjs from 'dayjs';
import AccordionCustom from '../components/ArcodionCustom';
import PageForPrint from '../components/PageForPrint';
import Printer from '../components/Printer';
import Table from '../components/Table';
import EmptyState from '../components/EmptyState';
import BarChart from '../components/charts/BarChart';
import Filter from '../components/Filter';
import Loading from '../components/Loading';
import Tabs from '../components/Tabs';
import Card from '../components/Card';
import fieldsTables from '../utils/fieldsTables';

interface CategoriaFinanceiraCurvaABC {
  codigo: number;
  descricao: string;
  valorPago: number;
  custoKg: number;
  custoArroba: number;
  custoCabMes: number;
  percentualCusto: number;
  percentualAcumulado: number;
  categoria: string;
}

interface barChartCurvaAbcData {
  xData: string;
  yData: number;
  rawData: CategoriaFinanceiraCurvaABC;
  name: string;
  color: string;
}

interface CardItem {
  title: string;
  value: string | number;
  big: boolean;
  tooltip?: string; 
}

type FarmsType = {
  proCodigo: number;
  proDescricao: string;
}

type FiltersPrintRefType = {
  espCodigo: string;
};

function IndicadoresCurvaABC() {
  const { translations, setLanguage } = useContext(LanguageContext);
  const [loading, setLoading] = useState<boolean>(true)
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<string>("");
  const [curvaAbcRawData, setCurvaAbcRawData] = useState<CategoriaFinanceiraCurvaABC[]>([]);
  const [curvaAbcData, setCurvaAbcData] = useState<CategoriaFinanceiraCurvaABC[]>([]);
  //const [pesoTotal, setPesoTotal] = useState<number>(0); removido temporariamente enquanto refatora a logica do peso médio
  const [barChartCurvaAbcData, setBarChartCurvaAbcData] = useState<barChartCurvaAbcData[]>([]);
  const [cardList, setCardList] = useState<CardItem[]>([]);
  const [farmName, setFarmName] = useState<string>("");
  const [filtersPrintRef, setFiltersPrintRef] = useState<FiltersPrintRefType | undefined>(undefined);
  const printRef1 = useRef(null);

  const getData = useCallback(async (filters: FiltersType) => {
    setLoading(true);
    const proCodigo = searchParams.get('pro_codigo') || '';
    const params = {
      proCodigo: proCodigo,
      espCodigo: filters.espCodigo === '' ? '0' : filters.espCodigo,
      dataIni: filters.startDate,
      dataFim: filters.endDate
    };
    
    const token = searchParams.get('token');

    // removido temporariamente enquanto refatora a logica do peso médio
    // const calculaCustosKg = (data: CategoriaFinanceiraCurvaABC[]) => {
    //   return data.map((categoria: CategoriaFinanceiraCurvaABC) => ({
    //     ...categoria,
    //     custoKg: pesoTotal > 0 ? Number(Number(categoria.valorPago).toFixed(2)) / pesoTotal : Number(categoria.valorPago)
    //   }));
    // };

    // const calculaCustosArroba = (data: CategoriaFinanceiraCurvaABC[]) => {
    //   return data.map((categoria: CategoriaFinanceiraCurvaABC) => ({
    //     ...categoria,
    //     custoArroba: pesoTotal > 0 ? Number((Number(categoria.valorPago) / pesoTotal / 30).toFixed(2)) : Number((Number(categoria.valorPago) / 30).toFixed(2))
    //   }));
    // };

    const calculaCustoCabMes = (data: CategoriaFinanceiraCurvaABC[], rebanhoMedio: number, periodoEmMesesValue: number) => {
      return data.map((categoria: CategoriaFinanceiraCurvaABC) => ({
        ...categoria,
        custoCabMes: rebanhoMedio > 0 ? Number((Number(categoria.valorPago) / rebanhoMedio / periodoEmMesesValue).toFixed(2)) : Number((Number(categoria.valorPago) / periodoEmMesesValue).toFixed(2))
      }));
    };

    const calculaCustoPercentuais = (data: CategoriaFinanceiraCurvaABC[], custoTotal: number) => {
      let acumulado = 0;

      const sortedData = [...data].sort((a, b) => Number(b.valorPago) - Number(a.valorPago));

      return sortedData.map((categoria: CategoriaFinanceiraCurvaABC) => {
        const costPercentage = (categoria.valorPago / custoTotal) * 100;
        const updatedData = {
          ...categoria,
          percentualCusto: Number(costPercentage.toFixed(2)),
          percentualAcumulado: Number((acumulado + costPercentage).toFixed(2))
        };
        acumulado += costPercentage;

        return updatedData;
      });
    };

    const selecionaCategorias = (data: CategoriaFinanceiraCurvaABC[]) => {
      return data.map((categoria: CategoriaFinanceiraCurvaABC) => ({
        ...categoria,
        categoria: categoria.percentualCusto > 50 ? 'A' : categoria.percentualAcumulado > 80 ? 'C' : categoria.percentualAcumulado > 50 ? 'B' : 'A'
      }));
    };

    const data = await getContasAPagarConfirmadas(params, token);
    const { espCodigo, ...paramsSemEsp } = params;
    const rebanhoMedioValue = await getRebanhoMedio(paramsSemEsp, token);

    if (data && rebanhoMedioValue && data.length > 0 && rebanhoMedioValue !== undefined) {
      setCurvaAbcRawData(data);

      const custoTotal = data.reduce((acc: number, curr: CategoriaFinanceiraCurvaABC) => acc + curr.valorPago, 0);

      const months = dayjs(params.dataFim).add(1, 'day').diff(dayjs(params.dataIni), 'month', true);
      const periodoEmMeses = Number(months.toFixed(2));

      //const pesoTotalValue = await getPesoTotal(params, token); removido temporariamente enquanto refatora a logica do peso médio
      //setPesoTotal(pesoTotalValue); removido temporariamente enquanto refatora a logica do peso médio
      
      const rebanhoMedio = rebanhoMedioValue ? Number(rebanhoMedioValue.toFixed(2)) : 1;
      
      let processedData = calculaCustoCabMes(data, rebanhoMedio, periodoEmMeses);
      //processedData = calculaCustosKg(processedData); removido temporariamente enquanto refatora a logica do peso médio
      //processedData = calculaCustosArroba(processedData); removido temporariamente enquanto refatora a logica do peso médio
      processedData = calculaCustoPercentuais(processedData, custoTotal);
      processedData = selecionaCategorias(processedData);

      setCurvaAbcData(processedData);

      const mappedData = processedData.map((categoria: CategoriaFinanceiraCurvaABC | any) => ({
        "xData": categoria.descricao,
        "yData": categoria.valorPago,
        "rawData": categoria,
        "name": translations ? translations["lbl.react.custo.total"] : "Custo Total",
        "color": categoria.categoria === "A" ? "#607D8B" : categoria.categoria === "B" ? "#1ABC9C" : "#8BC34A"
      }))
      setBarChartCurvaAbcData(mappedData);

      const generatedCardList = [
                {
          title: translations ? translations["lbl.react.soma.dos.custos.totais"] : "Soma dos custos totais",
          value: new Intl.NumberFormat('pt-BR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          }).format(custoTotal || 0),
          big: false
        },
        {
          title: translations ? translations["lbl.react.rebanho.medio"] : "Rebanho Médio",
          value: new Intl.NumberFormat('pt-BR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          }).format(rebanhoMedio || 0),
          big: true,
          tooltip: translations ? translations["lbl.react.tooltip.rebanho.medio"] : "O rebanho médio é calculado pela média aritmética simples do número de animais no início e no final do período selecionado."
        },
        {
          title: translations ? translations["lbl.react.periodo.em.meses"] : "Período em meses",
          value: periodoEmMeses.toFixed(2).replace('.', ','),
          big: false
        }
      ];
      setCardList(generatedCardList);
    } else {
      setCurvaAbcRawData([]);
      setCurvaAbcData([]);
      setBarChartCurvaAbcData([]);
    }

    const farmsResult = await getPropriedades(proCodigo, token).then((data: any) => data.filter((currFarm: FarmsType) => currFarm.proCodigo === Number(proCodigo))[0].proDescricao);
    setFarmName(farmsResult);

    setLoading(false);
  }, [searchParams, translations]);

  const handleChangeTab = (_event: React.SyntheticEvent, value: string) => {
    setCurvaAbcRawData([]);
    setSelectedTab(value);
  };

  const handleSearchButton = (filters: FiltersType) => {
    const mappedFilters = {
      ...filters,
      startDate: filters.startDate ? dayjs(filters.startDate).format("YYYY-MM-DD") : dayjs().startOf('year').format("YYYY-MM-DD"),
      endDate: filters.endDate ? dayjs(filters.endDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
    }

    getData(mappedFilters)
  }

  useEffect(() => {
    const languageParam = searchParams.get('language') || 'pt';
    const mappedFilters = {
      espCodigo: searchParams.get('espCodigo') || '',
      startDate: searchParams.get('dataIni') || dayjs().startOf('year').format("YYYY-MM-DD"),
      endDate: searchParams.get('dataFim') || dayjs().format("YYYY-MM-DD")
    }
    
    setFiltersPrintRef({
        'espCodigo': translations ? translations["lbl.react.filter.especie"] : "Espécie",
      });
    selectedTab === "" && setSelectedTab("curvaAbc");
    getData(mappedFilters);
    setLanguage(languageParam)
  }, [searchParams, setLanguage, selectedTab, translations, setFiltersPrintRef, getData]);

  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      const JSP_ORIGIN = process.env.REACT_APP_BASE_MANEJO || 'http://localhost:8080';
      window.parent.postMessage(
        { type: 'IFRAME_HEIGHT', height: height },
        JSP_ORIGIN
      );
    };

    sendHeight();

    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    window.addEventListener('resize', sendHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, []);

  return (
    <Grid container justifyContent="center">
      { translations &&
        <Grid item xs={12}>
          <div className="page-body">
            <span className="page-title">
              { translations["lbl.react.indicadores.financeiros"] }
            </span>
            <Grid container rowSpacing={2} justifyContent="center">
              <Grid item xs={12}>
                { selectedTab &&
                  <Tabs handleChange={handleChangeTab} value={selectedTab} labels={[{ label: translations["lbl.react.aba.curva.abc"], name: "curvaAbc" }]}/>
                }
              </Grid>
              <Grid item xs={12}>
                <Filter
                  handleSearch={handleSearchButton}
                  datePlaceholder={translations["lbl.react.data.pagamento"]}
                  selectedTab={selectedTab}
                  showFilters={showFilters.curva_abc}
                  onlyMonths={false}
                  dateFormat={"dd/MM/yyyy"}
                />
              </Grid>
              { loading === false ?
                <Grid item xs={12}>
                  {selectedTab === "curvaAbc" && (
                    curvaAbcRawData.length ?
                    <div>
                      <PageForPrint reference={printRef1} farmName={farmName} chartTitle={translations["lbl.react.aba.curva.abc"]} usedFilters={filtersPrintRef}>
                        <Grid container className="chart-container">
                          <Grid item xs={12}>
                            <AccordionCustom
                              title={translations["lbl.react.aba.curva.abc"]}
                              subtitle={translations["lbl.react.subtitle.curva.abc"]}
                              amountText={translations["lbl.react.categorias.financeiras"]}
                              intervalTotal={curvaAbcRawData ? curvaAbcRawData.length : 0}
                              topButtons={<Printer componentRef={printRef1} chartTitle={translations["lbl.react.aba.curva.abc"]} />}
                            >
                              <Grid container item xs={12} pb={2} mb={3}>
                                <BarChart
                                  data={barChartCurvaAbcData}
                                  barLabel={translations["lbl.react.custo.total"]}
                                  sideLabel={translations["lbl.react.categoria.financeira"]}
                                  shortenNames={true}
                                  customLegend={{
                                    verticalAlign: "bottom",
                                    align: "center",
                                    items: [
                                      { color: "#607D8B", label: `${translations["lbl.react.tabela.categoria"]} A (≤ 50% ${translations["lbl.react.tabela.maior.custo.individual"]})` },
                                      { color: "#1ABC9C", label: `${translations["lbl.react.tabela.categoria"]} B (> 50% ≤80%)` },
                                      { color: "#8BC34A", label: `${translations["lbl.react.tabela.categoria"]} C (> 80% ≤ 100%)` }
                                    ]
                                  }}
                                />
                              </Grid>
                            </AccordionCustom>
                            <Grid container item justifyContent='center' xs={12} pb={2} mb={3} className='card-container'>
                              {cardList && cardList.map((cardItem: CardItem) => {
                              return <Card title={cardItem.title} textValue={cardItem.value} colorType={cardItem.big ? "green" : "gray"} big={cardItem.big} tooltipText={cardItem.tooltip && cardItem.tooltip} />;
                              })}
                            </Grid>
                          </Grid>
                        </Grid>
                      </PageForPrint>
                      <Table
                        columns={fieldsTables(translations).curvaAbc}
                        rows={curvaAbcData}
                        idColumn='codigo'
                        sortColumn='categoria'
                        tableTitle={translations["lbl.react.dados.para.periodo.selecionado"]}
                        name="curvaAbc"
                        hasCheckbox={false}
                      />
                    </div>
                    :
                      <div>
                        <EmptyState />
                      </div>
                    )
                  }
                </Grid>
                :
                <Loading />
              }
            </Grid>
          </div>
        </Grid>
      }
    </Grid>
  );
}

export default IndicadoresCurvaABC;